import { assert } from 'cadenza/utils/custom-error';
import h from 'hyperscript';
import { openMenu, closeMenu } from 'ui/menu/menu';
import { debounce } from 'cadenza/utils/debounce';
import { on, unByKey } from 'cadenza/utils/event-util';
import { logger } from 'cadenza/utils/logging';
import { isKeyboardEnterClickEvent } from 'cadenza/utils/input-events';
import './multi-action-button.css';

/**
 * Wraps multiple actions into one button. Each action generates a menuItem. The icon and title of
 * the button itself reflects the currently active action.
 */
export class MultiActionButton extends HTMLElement {

  _actions;
  _button;
  _currentAction;
  _eventKeys;
  _icon;
  _isOpen;
  static NAME = 'd-multi-action-button';

  constructor (actions, { buttonId, activeAction = actions[0] } = {}) {
    super();
    assert(actions.length, 'actions must not be empty');
    this._actions = actions;
    this._button = h('button.button.button-icon.button-borderless');
    if (buttonId) {
      this._button.id = buttonId;
    }
    this.updateCurrentAction(activeAction);
  }

  connectedCallback () {
    this.append(this._button);
    this.classList.add(MultiActionButton.NAME);
    this._enableEventListeners();
  }

  disconnectedCallback () {
    this._disableEventListeners();
  }

  _enableEventListeners () {
    const debouncedOpen = debounce(() => this._open());
    this._eventKeys = [
      on(this, 'mouseenter', () => {
        debouncedOpen();
      }),
      on(this, 'mouseleave', () => {
        debouncedOpen.cancel();
        this._close();
      }),
      on(this._button, 'click', e => {
        // This listener is required only to show or close the menu on keyboard Enter press event.
        // For mouse users opening/closing is handled by the mouseenter and mouseleave event listeners above.
        if (!isKeyboardEnterClickEvent(e)) {
          return;
        }
        if (this._isOpen) {
          this._close();
        } else {
          this._open();
        }
      }),
      on(this._button, 'pointerdown', () => {
        // Prevent already open menu from closing while mouse button is pressed (same as in setupMenuButton() in menu.js)
        event.stopPropagation();
        const action = this._currentAction;
        if (action) {
          action.isSupported()
            .then(() => action.execute())
            .catch(error => logger.error('Cannot execute action', error));
        }
      })
    ];
  }

  _disableEventListeners () {
    unByKey(this._eventKeys);
    this._eventKeys = [];
  }

  set pressed (pressed) {
    this._button.setAttribute('aria-pressed', pressed);
  }

  get pressed () {
    return this._button.getAttribute('aria-pressed') === 'true';
  }

  set disabled (value) {
    this._button.disabled = value;
    this.setAttribute('aria-disabled', value);
    if (value) {
      this._disableEventListeners();
      this._close();
    } else {
      this._enableEventListeners();
    }
  }

  get disabled () {
    return this.getAttribute('aria-disabled') === 'true';
  }

  updateCurrentAction (action) {
    if (!action) {
      return;
    }
    this._currentAction = action;
    this._button.title = action.label;
    if (this._icon) {
      this._icon.remove();
    }
    this._icon = action.icon;
    this._button.append(this._icon);
  }

  _open () {
    if (this._isOpen) {
      return;
    }
    this._isOpen = true;
    this._menu = openMenu(
      this._button,
      () => this._makeMenuGroup(this._actions),
      {
        // appendTo: button so mouseleave isn't triggered when mouse moves to menu
        appendTo: this,
        onClose: () => {
          this._isOpen = false;
          this._button.removeAttribute('aria-has-popup');
        }
      }
    );
    this._button.setAttribute('aria-has-popup', 'true');
  }

  _close () {
    if (!this._isOpen) {
      return;
    }
    closeMenu();
  }

  _makeMenuGroup (actions) {
    return {
      groupType: 'radio',
      group: actions.map(action => this._makeMenuItem(action))
    };
  }

  _makeMenuItem (action) {
    return {
      label: action.label,
      icon: action.icon,
      disabled: !action.isSupportedSync(),
      checked: action === this._currentAction,
      execute: () => action.isSupported()
        .then(() => {
          this.updateCurrentAction(action);
          action.execute();
        })
        .catch(reason => logger.error('Cannot execute action', reason))
    };
  }

}
customElements.define(MultiActionButton.NAME, MultiActionButton);
